import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Button, Tooltip, Tabs } from 'antd';
import { ThemeProvider } from '@mui/material/styles';
import {
  WalletOutlined,
  RiseOutlined,
  AppstoreOutlined,
  DollarOutlined,
  ReloadOutlined,
  ArrowLeftOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  LineChartOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

import { ThemeContext } from '../../../context/ThemeContext';
import LandingLayout from '../LandingLayout';
import { usePortfolioUser } from './usePortfolioUser';
import { currency } from './portfolioHoldingPanels';
import PortfolioAnalyticsKpi from './PortfolioAnalyticsKpi';
import PortfolioCashFlowTab from './PortfolioCashFlowTab';
import PortfolioCorporateActionsTab from './PortfolioCorporateActionsTab';
import PortfolioAnalyticsTab from './PortfolioAnalyticsTab';
import PortfolioStressTestingTab from './PortfolioStressTestingTab';
import { createPortfolioMuiTheme } from './portfolioMuiTheme';
import { buildPortfolioAnalytics } from './portfolioAnalyticsUtils';
import apiClient from '../../../services/apiClient';
import './MyPortfolioDashboardPage.css';
import '../Instruments.css';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

function MyPortfolioDashboardPage() {
  const { isDark } = useContext(ThemeContext);
  const muiTheme = useMemo(() => createPortfolioMuiTheme(isDark), [isDark]);
  const navigate = useNavigate();
  const { userId, isAuthenticated, loading: userLoading } = usePortfolioUser();

  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('cash-flow');

  const loadHoldings = useCallback(async () => {
    if (!userId) {
      setHoldings([]);
      setLoaded(true);
      return;
    }
    try {
      setLoading(true);
      const res = await apiClient.get(`${BASE_URL}/user/portfolio/list/${userId}`);
      setHoldings(res.data?.data || []);
    } catch (e) {
      console.error('Failed to load portfolio holdings', e);
      setHoldings([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [userId]);

  const handleRefreshPrices = useCallback(async () => {
    if (!userId) return;
    try {
      setRefreshing(true);
      await apiClient.post(`${BASE_URL}/user/portfolio/refresh-prices/${userId}`);
      await loadHoldings();
    } catch (e) {
      console.error('Failed to refresh portfolio prices', e);
    } finally {
      setRefreshing(false);
    }
  }, [userId, loadHoldings]);

  useEffect(() => {
    if (userLoading) return;
    if (!userId) {
      setHoldings([]);
      setLoaded(true);
      return;
    }
    setLoaded(false);
    loadHoldings();
  }, [userId, userLoading, loadHoldings]);

  const analytics = useMemo(
    () => buildPortfolioAnalytics(holdings),
    [holdings]
  );

  const kpiItems = useMemo(() => [
    {
      key: 'mv',
      icon: <DollarOutlined />,
      label: 'Total market value',
      value: currency(analytics.totalMV),
      accent: '#9fe870',
      wide: true,
    },
    {
      key: 'yield',
      icon: <RiseOutlined />,
      label: 'Average yield',
      value: analytics.avgYield != null ? `${Number(analytics.avgYield).toFixed(2)}%` : '—',
      accent: '#378ADD',
    },
    {
      key: 'coupon',
      icon: <AppstoreOutlined />,
      label: 'Avg coupon',
      value: analytics.avgCoupon != null ? `${Number(analytics.avgCoupon).toFixed(2)}%` : '—',
      accent: '#fbbf24',
    },
    {
      key: 'count',
      icon: <AppstoreOutlined />,
      label: 'Holdings',
      value: String(analytics.holdingsCount),
      sub: `${analytics.activeStrategies} active strateg${analytics.activeStrategies === 1 ? 'y' : 'ies'}`,
      accent: '#67e8f9',
    },
  ], [analytics]);

  const hasHoldings = loaded && holdings.length > 0;
  const pageLoading = userLoading || (isAuthenticated && !loaded && loading);

  const handleRowsChanged = useCallback((nextRows) => {
    if (Array.isArray(nextRows)) {
      setHoldings(nextRows);
    }
  }, []);

  const tabItems = useMemo(() => [
    {
      key: 'cash-flow',
      label: (
        <span className="mpd-tab-label">
          <CalendarOutlined /> Cash flow
        </span>
      ),
      children: <PortfolioCashFlowTab holdings={holdings} />,
    },
    {
      key: 'corporate-actions',
      label: (
        <span className="mpd-tab-label">
          <InfoCircleOutlined /> Corporate actions
        </span>
      ),
      children: <PortfolioCorporateActionsTab holdings={holdings} />,
    },
    {
      key: 'analytics',
      label: (
        <span className="mpd-tab-label">
          <LineChartOutlined /> Analytics
        </span>
      ),
      children: (
        <PortfolioAnalyticsTab
          analytics={analytics}
          holdings={holdings}
          loading={loading}
          refreshing={refreshing}
          onRequestReload={loadHoldings}
          onRowsChanged={handleRowsChanged}
          onNavigate={navigate}
        />
      ),
    },
    {
      key: 'stress-testing',
      label: (
        <span className="mpd-tab-label">
          <ExperimentOutlined /> Stress testing
        </span>
      ),
      children: <PortfolioStressTestingTab />,
    },
  ], [holdings, analytics, loading, refreshing, loadHoldings, handleRowsChanged, navigate]);

  return (
    <LandingLayout>
      <div className={`mpd-page isin-page${isDark ? ' dark' : ''}`}>
        <div className="mpd-hero-glow" aria-hidden />

        <div className="container mpd-header-wrap">
          <header className="mpd-header">
            <div className="mpd-header-top">
              <button
                type="button"
                className="mpd-back-link"
                onClick={() => navigate('/construct-portfolio')}
              >
                <ArrowLeftOutlined /> Construct portfolio
              </button>
              {isAuthenticated && hasHoldings && (
                <Tooltip title="Refresh indicative prices from market data">
                  <Button
                    icon={<ReloadOutlined spin={refreshing} />}
                    loading={refreshing}
                    onClick={handleRefreshPrices}
                    className="mpd-refresh-btn"
                  >
                    Refresh prices
                  </Button>
                </Tooltip>
              )}
            </div>
            <h1 className="mpd-title">
              <WalletOutlined className="mpd-title-icon" />
              My Portfolio
            </h1>
            <p className="mpd-subtitle">
              Track cash flows, corporate actions, analytics, and stress scenarios across your bond holdings.
            </p>
          </header>
        </div>

        {pageLoading && (
          <div className="mpd-loading">
            <Spin size="large" />
            <p>Loading your portfolio…</p>
          </div>
        )}

        {!userLoading && !isAuthenticated && loaded && (
          <div className="container">
            <div className={`mpd-empty${isDark ? ' mpd-empty--dark' : ''}`}>
              <p className="mpd-empty-title">Log in to view your portfolio</p>
              <p className="mpd-empty-hint">
                Sign in to see cash flows, corporate actions, analytics, and all saved bond holdings.
              </p>
              <button
                type="button"
                className="mpd-primary-btn"
                onClick={() => navigate('/login', { state: { from: '/my-portfolio' } })}
              >
                Log in →
              </button>
            </div>
          </div>
        )}

        {loaded && isAuthenticated && !pageLoading && !hasHoldings && (
          <div className="container">
            <div className={`mpd-empty${isDark ? ' mpd-empty--dark' : ''}`}>
              <p className="mpd-empty-title">Your portfolio is empty</p>
              <p className="mpd-empty-hint">
                Pick a strategy and add bonds from portfolio construction — tabs will populate automatically.
              </p>
              <button
                type="button"
                className="mpd-primary-btn"
                onClick={() => navigate('/construct-portfolio')}
              >
                Choose a strategy →
              </button>
            </div>
          </div>
        )}

        {loaded && isAuthenticated && hasHoldings && (
          <ThemeProvider theme={muiTheme}>
            <section className="container mpd-kpi-section">
              <PortfolioAnalyticsKpi items={kpiItems} />
            </section>

            <section className="container mpd-tabs-section">
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                className="mpd-portfolio-tabs"
                destroyInactiveTabPane={false}
              />
            </section>
          </ThemeProvider>
        )}
      </div>
    </LandingLayout>
  );
}

export default MyPortfolioDashboardPage;
